import { Layer, Source, useMap } from 'react-map-gl';
import { useSelector } from 'react-redux';
import { useNoTileDataToast } from '../../hooks/map/useNoTileDataToast';
import { basemapSelector } from '../../store/selectors/general';
import { getBeforeIdForLayer, getContourColor } from '../../utils/map';

const API_ROOT = process.env.REACT_APP_API_ROOT || '/api';

export const VectorTileRenderer = ({ filterInfo, config, isVisible, opacity, layerId }) => {
  const { current: map } = useMap(); // add this
  const mapInstance = map?.getMap?.();
  const beforeId = getBeforeIdForLayer(layerId, mapInstance);
  const basemapInfo = useSelector(basemapSelector);

  // This is used to show toast when there is no contour data available for the area the user is zoomed to
  useNoTileDataToast({
    map: filterInfo.selectedCounty ? mapInstance : null,
    sourceId: config && `vector-source-${config.layerName}`,
    sourceLayer: config?.layerName,
    message: config?.noDataMessage,
    minZoom: config?.minDataZoom,
  });

  if (!config || !filterInfo.selectedCounty) return null;

  if (layerId === 'terrainContour') {
    return (
      <Source id="terrain-contour-source" type="vector" url="mapbox://mapbox.mapbox-terrain-v2">
        <Layer
          id="terrain-contour"
          type="line"
          source-layer="contour"
          paint={{
            'line-color': getContourColor(basemapInfo),
            'line-width': 2,
            'line-opacity': 0.8,
          }}
          beforeId={beforeId}
        />
        <Layer
          id="terrain-contour-label"
          type="symbol"
          source-layer="contour"
          layout={{
            'symbol-placement': 'line',
            'text-field': [
              'concat',
              ['to-string', ['round', ['*', ['get', 'ele'], 3.28084]]],
              ' ft',
            ],
            'text-size': 16,
          }}
          paint={{
            'text-color': '#1f2023',
            'text-halo-color': '#ffffff',
            'text-halo-width': 1,
          }}
          beforeId={beforeId}
        />
      </Source>
    );
  }

  const tileUrl = `${API_ROOT}/map/${config.url}/{z}/{x}/{y}.mvt?county=${filterInfo.selectedCounty}`;

  const vis = isVisible ? opacity : 0;
  const isExposureLayer = ['existing_exposure', 'future_exposure'].includes(config.layerName);
  const isContoursLayer = layerId === 'contours2ftTile';

  const getContourFillColor = () => {
    return basemapInfo === 'light' ? '#555555' : '#ffffff';
  };

  const circlePaint = {
    'circle-color': config.style?.fillColor ?? config.style?.color ?? '#7f8c8d',
    'circle-opacity': vis,
    'circle-stroke-width': 1,
    'circle-stroke-color': config.style?.color ?? '#7f8c8d',
    'circle-stroke-opacity': vis,
    ...(isExposureLayer
      ? {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 1, 12, 2.5, 14, 3.5, 16, 5],
        }
      : {
          'circle-radius': config.style?.radius ?? 4,
        }),
  };

  const fillPaint = {
    'fill-color': isContoursLayer ? getContourFillColor() : (config.style?.fillColor ?? '#3388ff'),
    'fill-opacity': vis * (config.style?.fillOpacity ?? 0.6),
  };

  const linePaint = {
    'line-color': isContoursLayer
      ? getContourColor(basemapInfo)
      : (config.style?.color ?? '#3388ff'),
    'line-width': config.style?.weight ?? 2,
    'line-opacity': vis,
  };

  // Elevation labels drawn along the 2ft contour lines; label text is formatted by the API
  const contourLabelPaint = {
    'text-color': basemapInfo === 'light' ? '#1f2023' : '#ffffff',
    'text-halo-color': basemapInfo === 'light' ? '#ffffff' : '#1f2023',
    'text-halo-width': 1,
    'text-opacity': vis,
  };

  // Invisible wider line for better click detection on thin lines (like contours)
  const lineHitAreaPaint = {
    'line-color': isContoursLayer
      ? getContourColor(basemapInfo)
      : (config.style?.color ?? '#3388ff'),
    'line-width': (config.style?.weight ?? 2) + 8, // 8px wider for easier clicking
    'line-opacity': 0, // Invisible
  };

  return (
    <Source id={`vector-source-${config.layerName}`} type="vector" tiles={[tileUrl]} maxzoom={18}>
      <Layer
        id={`${config.layerName}-fill`}
        type="fill"
        source-layer={config.layerName}
        filter={['in', ['geometry-type'], ['literal', ['Polygon', 'MultiPolygon']]]}
        paint={fillPaint}
        beforeId={beforeId}
      />
      {/* Invisible wider line for better click detection */}
      <Layer
        id={`${config.layerName}-line-hitarea`}
        type="line"
        source-layer={config.layerName}
        filter={[
          'in',
          ['geometry-type'],
          ['literal', ['LineString', 'MultiLineString', 'Polygon', 'MultiPolygon']],
        ]}
        paint={lineHitAreaPaint}
        beforeId={beforeId}
      />
      {/* Visible line on top */}
      <Layer
        id={`${config.layerName}-line`}
        type="line"
        source-layer={config.layerName}
        filter={[
          'in',
          ['geometry-type'],
          ['literal', ['LineString', 'MultiLineString', 'Polygon', 'MultiPolygon']],
        ]}
        paint={linePaint}
        beforeId={beforeId}
      />
      {isContoursLayer && (
        <Layer
          id={`${config.layerName}-label`}
          type="symbol"
          source-layer={config.layerName}
          filter={['in', ['geometry-type'], ['literal', ['LineString', 'MultiLineString']]]}
          layout={{
            'symbol-placement': 'line',
            'symbol-spacing': 300,
            'text-field': ['get', 'label'],
            'text-size': 12,
            'text-max-angle': 30,
            'text-padding': 4,
          }}
          paint={contourLabelPaint}
          beforeId={beforeId}
        />
      )}
      <Layer
        id={`${config.layerName}-circle`}
        type="circle"
        source-layer={config.layerName}
        filter={['in', ['geometry-type'], ['literal', ['Point', 'MultiPoint']]]}
        paint={circlePaint}
        beforeId={beforeId}
      />
    </Source>
  );
};
